import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@f2e/ui/components/base"
import { ContextProviderDisplayName } from "@/types"

import { ProviderLogo } from "./ProviderLogo"

interface ProviderBadge {
  provider: string
  count: number
}

export interface ProviderBadgesProps {
  providers: string[]
  defaultSelected?: string
  onClick?: (provider: string) => void
}

const ProviderBadges = ({
  providers,
  defaultSelected = "all",
  onClick,
}: ProviderBadgesProps) => {
  const [badges, setBadges] = useState<ProviderBadge[]>([])
  const [selected, setSelected] = useState<string>(defaultSelected)
  const { t } = useTranslation()

  const handleClick = (provider: string) => {
    setSelected(provider)
    onClick?.(provider)
  }

  useEffect(() => {
    const newBadges: ProviderBadge[] = []
    providers.forEach((provider) => {
      const found = newBadges.find((badge) => badge.provider === provider)
      if (found) {
        found.count += 1
      } else {
        newBadges.push({ provider: provider, count: 1 })
      }
    })
    setBadges(newBadges)
  }, [providers])

  useEffect(() => {
    setSelected(defaultSelected)
  }, [defaultSelected])

  return (
    <div className="flex flex-wrap gap-2">
      <Badge
        key={-1}
        className="cursor-pointer"
        onClick={() => handleClick("all")}
        variant={selected === "all" ? "default" : "secondary"}
      >
        <ProviderLogo provider="all" />
        {t("workspace.all", { count: providers.length })}
      </Badge>
      {badges.map((badge, idx) => (
        <Badge
          key={idx}
          className="cursor-pointer"
          onClick={() => handleClick(badge.provider)}
          variant={selected === badge.provider ? "default" : "secondary"}
        >
          <ProviderLogo provider={badge.provider} />
          {ContextProviderDisplayName[badge.provider] || badge.provider}(
          {badge.count})
        </Badge>
      ))}
    </div>
  )
}

export { ProviderBadges }
export default ProviderBadges
